import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useActiveCustomerPermissions } from '@/hooks/useActiveCustomerPermissions';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/use-organization';
import { useGroupsUnreadTotal } from '@/hooks/useGroupsUnreadTotal';
import { formatUnreadBadge } from '@/lib/groups/groupUnread';
import { cn } from '@/lib/utils';
import {
  Target,
  Users,
  Stethoscope,
  GraduationCap,
  Trophy,
  Settings,
  Plane,
  Heart,
  Home,
  Menu,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: 'navigation' | 'habitTracking' | 'supportGroups' | 'coachingSessions' | 'miniCourses' | 'changeTrips' | 'treatmentAid' | 'pointsRewards' | 'settings';
}

const navItems: NavItem[] = [
  {
    label: 'dashboard.navigation.home',
    href: '/dashboard/customer',
    icon: Home,
    // No permission check for home page
  },
  {
    label: 'dashboard.navigation.habitTracking',
    href: '/dashboard/customer/habits',
    icon: Target,
    permission: 'habitTracking',
  },
  {
    label: 'dashboard.navigation.supportGroups',
    href: '/dashboard/customer/support',
    icon: Users,
    permission: 'supportGroups',
  },
  {
    label: 'dashboard.navigation.coachingSessions',
    href: '/dashboard/customer/coaching',
    icon: Stethoscope,
    permission: 'coachingSessions',
  },
  {
    label: 'dashboard.navigation.miniCourses',
    href: '/dashboard/customer/education',
    icon: GraduationCap,
    permission: 'miniCourses',
  },
  {
    label: 'dashboard.navigation.trips',
    href: '/dashboard/customer/trips',
    icon: Plane,
    permission: 'changeTrips',
  },
  {
    label: 'dashboard.navigation.treatmentSupport',
    href: '/dashboard/customer/treatment',
    icon: Heart,
    permission: 'treatmentAid',
  },
  {
    label: 'dashboard.navigation.pointsRewards',
    href: '/dashboard/customer/points',
    icon: Trophy,
    permission: 'pointsRewards',
  },
  {
    label: 'dashboard.navigation.settings',
    href: '/dashboard/customer/settings',
    icon: Settings,
    permission: 'settings',
  }
];

export function CustomerNavigation() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasSectionAccess } = useActiveCustomerPermissions();
  const [isOpen, setIsOpen] = useState(false);
  const { currentUser } = useAuth();
  const { selectedOrganization } = useOrganization();
  // Unread chat messages in the member's groups of the active organization
  // (namat#36 P2), shown on the support groups entry.
  const groupsUnread = useGroupsUnreadTotal(
    currentUser?.uid,
    selectedOrganization?.id,
    hasSectionAccess('navigation') && hasSectionAccess('supportGroups'),
  );
  const unreadBadge = formatUnreadBadge(groupsUnread);
  const renderUnread = (href: string) =>
    href === '/dashboard/customer/support' && unreadBadge ? (
      <span
        className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#7dda55] text-white text-[0.7rem] font-bold leading-5 text-center"
        aria-label={`${unreadBadge} ${t('support.unreadMessages')}`}
      >
        {unreadBadge}
      </span>
    ) : null;

  // Check if user has access to navigation at all
  if (!hasSectionAccess('navigation')) {
    return null;
  }

  // Filter navigation items based on user permissions
  const visibleNavItems = navItems.filter(item => {
    // Always show home page
    if (!item.permission) return true;
    // Check permission for other pages
    return hasSectionAccess(item.permission);
  });

  const handleNavigation = (href: string) => {
    navigate(href);
    setIsOpen(false);
  };

  return (
    <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-[100rem] mx-auto px-4 flex justify-center">
        {/* Desktop Navigation */}
        <div className="hidden md:flex h-14 items-center gap-4 overflow-x-auto">
          {visibleNavItems.map((item) => (
            <button
              key={item.href}
              onClick={() => handleNavigation(item.href)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 text-[0.91rem] font-medium transition-colors hover:text-primary",
                location.pathname === item.href
                  ? "text-primary"
                  : "text-muted-foreground",
                language === 'en' && "text-[0.81rem]"
              )}
            >
              <item.icon className="h-4 w-4" />
              <span>{t(item.label)}</span>
              {renderUnread(item.href)}
            </button>
          ))}
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden flex h-14 items-center justify-between">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 relative">
                <Menu className="h-5 w-5" />
                {unreadBadge && hasSectionAccess('supportGroups') && (
                  <span className="absolute top-1 end-1 h-2 w-2 rounded-full bg-[#7dda55]" aria-hidden="true" />
                )}
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side={language === 'ar' ? 'right' : 'left'} className="w-[280px] sm:w-[350px]">
              <SheetHeader>
                <SheetTitle>{t('dashboard.navigation.menu')}</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-2 mt-6">
                {visibleNavItems.map((item) => (
                  <button
                    key={item.href}
                    onClick={() => handleNavigation(item.href)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors hover:text-primary rounded-md",
                      location.pathname === item.href
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted"
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{t(item.label)}</span>
                    {renderUnread(item.href)}
                  </button>
                ))}
              </div>
            </SheetContent>
          </Sheet>

          {/* Show current page title on mobile */}
          <div className="flex-1 text-center">
            <span className="text-sm font-medium">
              {t(visibleNavItems.find(item => item.href === location.pathname)?.label || 'dashboard.navigation.home')}
            </span>
          </div>

          {/* Empty div for flex spacing */}
          <div className="w-9" />
        </div>
      </div>
    </nav>
  );
}